import React from 'react';
import { FormattedMessage } from 'react-intl';
import { translations } from '@/locale';
import ActivityBadge from '@/components/Badge/ActivityBadge';
import PaginatedTable from '@/components/ui/paginated';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import Typography from '@/components/Typography';
import { urls } from '@/constants';
import { Role } from '@/domains/user';
import { Page } from '@/domains';
import { Banknote, Shield, User } from 'lucide-react';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: Role;
  active: boolean;
  createdAt: string;
}

interface UserListTableProps {
  source: (page: number, size: number) => Promise<Page<User>>;
}

const UserListTable: React.FC<UserListTableProps> = ({ source }) => {
  return (
    <div className="space-y-4">
      <PaginatedTable<string, User>
        source={source}
        renderHeader={() => (
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
        )}
      >
        {(users) => (
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id} url={urls.users.detail.get(user.id)}>
                <TableCell>
                  <Typography is="p">
                    {user.firstName} {user.lastName}
                  </Typography>
                  <Typography is="span" size="sm" className="text-muted-foreground">
                    {user.email}
                  </Typography>
                </TableCell>
                <TableCell>
                  <RoleDisplay role={user.role} />
                </TableCell>
                <TableCell>{new Date(user.createdAt).toLocaleDateString()}</TableCell>
                <TableCell>
                  <ActivityBadge active={user.active} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        )}
      </PaginatedTable>
    </div>
  );
};

const RoleDisplay: React.FC<{ role: Role }> = ({ role }) => {
  let icon;
  let label;

  switch (role) {
    case Role.ADMIN:
      icon = <Shield className="text-blue-600 w-5 h-5" />;
      label = 'Admin';
      break;
    case Role.ORG_MEMBER:
      icon = <User className="text-gray-600 w-5 h-5" />;
      label = 'Customer';
      break;
    case Role.VENDOR_CASHIER:
      icon = <Banknote className="text-green-600 w-5 h-5" />;
      label = 'Cashier';
      break;
    default:
      icon = null;
      label = 'Unknown';
  }

  return (
    <div className="flex items-center gap-2">
      {icon}
      <span>{label}</span>
    </div>
  );
};

export default UserListTable;
